import { Box, Stack, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { onboardingTokens } from "../../theme";

/**
 * One selectable goal on the goal-selection step.
 *
 * All five cards are this component. They differ only in their copy and icon,
 * so the selected treatment, the hit area and the accessible name are defined
 * once rather than five times.
 *
 * Rendered as a real `<button>` with `aria-pressed`, which is the toggle-button
 * pattern: a screen reader announces the label and whether it is currently
 * pressed, and keyboard activation comes for free. A clickable `<div>` would
 * need role, tabIndex and key handling reimplemented to reach the same place.
 *
 * The selected state is carried by three signals — border, filled indicator and
 * a tick — deliberately not by colour alone, which ECH-116's review checklist
 * calls out.
 *
 * @param {object} props
 * @param {string} props.label
 * @param {string} props.description
 * @param {import('react').ReactNode} props.icon
 * @param {boolean} props.selected
 * @param {() => void} props.onToggle
 */
const GoalCard = ({ label, description, icon, selected, onToggle }) => (
  <Box
    component="button"
    type="button"
    onClick={onToggle}
    aria-pressed={selected}
    data-testid="goal-card"
    data-goal-selected={selected ? "true" : "false"}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 2,
      width: "100%",
      p: 2,
      textAlign: "left",
      cursor: "pointer",
      borderRadius: "0.75rem",
      backgroundColor: selected
        ? onboardingTokens.lightTeal
        : onboardingTokens.card,
      border: `1.5px solid ${
        selected ? onboardingTokens.teal : onboardingTokens.track
      }`,
      boxShadow: selected ? "0 0 0 3px rgba(1, 190, 204, 0.1)" : "none",
      transition: "border-color 0.15s, background-color 0.15s",
      "&:hover": { borderColor: onboardingTokens.teal },
      "&:focus-visible": {
        outline: `2px solid ${onboardingTokens.teal}`,
        outlineOffset: 2,
      },
    }}
  >
    {/* Fixed size and no shrinking, so a longer description cannot squeeze the
        icon and drag the text out of line with the other cards. */}
    <Box
      aria-hidden="true"
      sx={{
        flexShrink: 0,
        width: 40,
        height: 40,
        borderRadius: "0.5rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: selected
          ? onboardingTokens.teal
          : onboardingTokens.muted,
        color: selected ? "primary.contrastText" : onboardingTokens.navy,
      }}
    >
      {icon}
    </Box>

    <Stack sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontWeight: 600, fontSize: "0.9375rem" }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: "0.8125rem",
          color: onboardingTokens.mutedForeground,
          lineHeight: 1.5,
        }}
      >
        {description}
      </Typography>
    </Stack>

    {/* The non-colour signal: an empty ring becomes a filled tick. */}
    <Box
      aria-hidden="true"
      sx={{
        flexShrink: 0,
        width: 20,
        height: 20,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: selected ? onboardingTokens.teal : "transparent",
        border: `1.5px solid ${
          selected ? onboardingTokens.teal : onboardingTokens.mutedForeground
        }`,
      }}
    >
      {selected && (
        <CheckIcon sx={{ fontSize: 14, color: "primary.contrastText" }} />
      )}
    </Box>
  </Box>
);

export default GoalCard;
